import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { StoreState } from '../../../../store/createStore';
import { calculateShippingRatesSpecificRequest, selectShippingOption } from '../../../../store/modules/parameter/actions';
import { checkoutSetShippingPrice } from '../../../../store/modules/checkout/actions';
import { UserAddressShipping } from '../../../../store/modules/auth/types';
import Loading from '../../../../components/Loading';
import { getShippingPrice } from '../../../../utils/formatPrice';

import {
  ShippingOptionsContainer,
  ShippingCard,
  ShippingInfo,
  ShippingTitle,
  ShippingDescription,
  ShippingPrice,
  PriceValue,
  DeliveryTime,
  RadioButton,
  LoadingContainer,
  // ErrorContainer,
  AddressInfo,
  AddressText,
  CardHeader,
  CardContent,
} from './styles';
import { useTranslation } from '../../../../hooks/useTranslation';
import { useCheckoutInfo } from '../../../../hooks/useCheckoutInfo';
import { config } from '../../../../config';

interface ShippingOptionsProps {
  onShippingSelect?: (option: UserAddressShipping) => void;
  headerButton?: React.ReactNode;
}

const ShippingOptions: React.FC<ShippingOptionsProps> = ({
  onShippingSelect,
  headerButton,
}) => {
  const { onTranslation: t } = useTranslation();
  const dispatch = useDispatch();
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("");

  const { onSetaddressSelect } = useCheckoutInfo();
  const {
    shippingOptions,
    selectedShippingOption,
    loading,
    address,
  } = useSelector((state: StoreState) => state.parameter);

  const formatDateTime = (date: Date, time: string): string => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year} ${time}`;
  };

  const timeToMinutes = (time: string): number => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const isDayAvailable = (date: Date, daysOpened: string[]): boolean => {
    const dayOfWeek = date.getDay();
    return daysOpened.includes(dayOfWeek.toString());
  };

  const getMinimumAvailableDateTime = (shippingInfo: UserAddressShipping) => {
    const now = new Date();
    const minimumDateTime = new Date(now.getTime() + config.minimumHoursOffSetShippingSchedule * 60 * 60 * 1000);
    
    const openingMinutes = timeToMinutes(shippingInfo.opening_hours);
    const closingMinutes = timeToMinutes(shippingInfo.closing_hours);
    
    let searchDate = new Date(minimumDateTime);
    searchDate.setHours(0, 0, 0, 0);
    
    const maxDays = typeof shippingInfo.max_delivery_days === 'string' 
      ? parseInt(shippingInfo.max_delivery_days) 
      : shippingInfo.max_delivery_days;
    
    for (let i = 0; i <= maxDays; i++) {
      const currentDate = new Date(searchDate);
      currentDate.setDate(searchDate.getDate() + i);
      
      if (!isDayAvailable(currentDate, shippingInfo.days_opened)) {
        continue;
      }
      
      if (currentDate.toDateString() === minimumDateTime.toDateString()) {
        const minimumMinutes = minimumDateTime.getHours() * 60 + minimumDateTime.getMinutes();
        
        if (minimumMinutes < closingMinutes) {
          const firstAvailableMinutes = Math.max(minimumMinutes, openingMinutes);
          
          if (firstAvailableMinutes < closingMinutes) {
            return {
              date: currentDate,
              minHour: Math.ceil(firstAvailableMinutes / 60)
            };
          }
        }
      } else if (currentDate > minimumDateTime) {
        return {
          date: currentDate,
          minHour: Math.floor(openingMinutes / 60)
        };
      }
    }
    
    return null;
  };

  const calculateShippingRate = (date: Date, time: string) => {
    if (!selectedShippingOption) return;
    
    const formattedDateTime = formatDateTime(date, time);
    
    dispatch(calculateShippingRatesSpecificRequest({
      selectedShippingOption: {
        ...selectedShippingOption,
        chosen_shipping_date: formattedDateTime
      },
      addressId: address?.id as number,
      postcode: address?.postcode as string,
      number: address?.number as string,
      setaddressSelect: onSetaddressSelect,
    }));
  };

  const handleShippingSelect = (option: UserAddressShipping) => {
    if (option.id === selectedShippingOption?.id) return;
    
    setSelectedDate("");
    setSelectedTime("");
    
    dispatch(selectShippingOption(option));
    dispatch(checkoutSetShippingPrice(option.price || 0));

    if (onShippingSelect) {
      onShippingSelect(option);
    }
  };

  const formatDateForDisplay = (date: Date): string => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const generateValidDates = (shippingInfo: UserAddressShipping): Date[] => {
    const dates: Date[] = [];
    const minAvailable = getMinimumAvailableDateTime(shippingInfo);
    
    if (!minAvailable) return dates;
    
    const startDate = new Date(minAvailable.date);
    const maxDays = typeof shippingInfo.max_delivery_days === 'string'
      ? parseInt(shippingInfo.max_delivery_days)
      : shippingInfo.max_delivery_days;
    
    for (let i = 0; i <= maxDays * 2; i++) { 
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + i);
      
      if (isDayAvailable(currentDate, shippingInfo.days_opened)) {
        dates.push(currentDate);
        
        if (dates.length >= maxDays) break;
      }
    }
    
    return dates;
  };

  const generateHourOptions = (shippingInfo: UserAddressShipping, selectedDateObj: Date | null): string[] => {
    if (!selectedDateObj) return [];
    
    const now = new Date();
    const openingMinutes = timeToMinutes(shippingInfo.opening_hours);
    const closingMinutes = timeToMinutes(shippingInfo.closing_hours);
    const minAvailable = getMinimumAvailableDateTime(shippingInfo);
    
    let startHour = Math.floor(openingMinutes / 60);
    const endHour = Math.floor(closingMinutes / 60);

    const isToday = selectedDateObj.toDateString() === now.toDateString();
    if (isToday && minAvailable && selectedDateObj.toDateString() === minAvailable.date.toDateString()) {
      startHour = Math.max(startHour, minAvailable.minHour);
    }
    
    const hours: string[] = [];
    
    for (let i = startHour; i <= endHour; i++) {
      const hourString = `${String(i).padStart(2, '0')}:00`;

      const hourMinutes = i * 60;
      if (hourMinutes >= openingMinutes && hourMinutes <= closingMinutes) {
        hours.push(hourString);
      }
    }
    
    return hours;
  };

  if (loading) {
    return (
      <LoadingContainer>
        <Loading size={40} />
      </LoadingContainer>
    );
  }

  return (
    <ShippingOptionsContainer>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          margin: '0 0 20px 0',
          gap: '24px',
        }}
      >
        <h4 style={{ margin: '0', color: '#333', flex: '1' }}>
          Opções de Entrega
        </h4>
        <div style={{ flexShrink: 0 }}>{headerButton}</div>
      </div>

      {shippingOptions?.map(option => {
        const isSelected = option.id === selectedShippingOption?.id;
        const isScheduled = option.is_scheduled;

        return (
          <ShippingCard
            key={`${option.carrier}-${option.method}`}
            selected={isSelected}
            onClick={() => {handleShippingSelect(option)}}
          >
            <CardHeader>
              <CardContent>
                <RadioButton selected={isSelected} />

                <ShippingInfo>
                  <ShippingTitle>{option.method_title}</ShippingTitle>
                  <ShippingDescription>
                    {option.method_description || `via ${option.carrier_title}`}
                  </ShippingDescription>
                </ShippingInfo>
              </CardContent>

              <ShippingPrice>
                <PriceValue>{getShippingPrice(option)}</PriceValue>
                {option.delivery_time && (
                  <DeliveryTime>{option.delivery_time}</DeliveryTime>
                )}
              </ShippingPrice>
            </CardHeader>

            {isSelected && isScheduled && (() => {
              const validDates = generateValidDates(option);
              const selectedDateObj = selectedDate 
                ? validDates.find(d => formatDateForDisplay(d) === selectedDate) || null
                : null;
              const availableHours = generateHourOptions(option, selectedDateObj);
              
              return (
                <div style={{
                  display: "flex",
                  justifyContent: 'center',
                  gap: "10px",
                  margin: "10px 0px 10px 0px"
                }}>
                  <select
                    value={selectedDate}
                    onChange={(e) => {
                      const newDate = e.target.value;
                      setSelectedDate(newDate);
                      
                      if (selectedTime) {
                        const newDateObj = validDates.find(d => formatDateForDisplay(d) === newDate);
                        if (newDateObj) {
                          const availableHoursForNewDate = generateHourOptions(option, newDateObj);
                          
                          if (!availableHoursForNewDate.includes(selectedTime)) {
                            setSelectedTime("");
                          } else {
                            calculateShippingRate(newDateObj, selectedTime);
                          }
                        }
                      }
                    }}
                    style={{
                      border: "1px solid #ccc",
                      padding: "8px 12px",
                      borderRadius: "999px",
                      appearance: "none",
                      fontSize: "14px",
                      minWidth: "140px",
                      cursor: "pointer",
                      backgroundColor: "#fff"
                    }}
                  >
                    <option value="">
                      {t('main.new_checkout.view_checkout.shippingOption.select_date')}
                    </option>
                    {validDates.map((date) => {
                      const dateFormatted = formatDateForDisplay(date);
                      return (
                        <option key={date.toISOString()} value={dateFormatted}>
                          {dateFormatted}
                        </option>
                      );
                    })}
                  </select>

                  <select
                    value={selectedTime}
                    onChange={(e) => {
                      const newTime = e.target.value;
                      setSelectedTime(newTime);
                      
                      if (selectedDateObj && newTime) {
                        calculateShippingRate(selectedDateObj, newTime);
                      }
                    }}
                    style={{
                      border: "1px solid #ccc",
                      padding: "8px 12px",
                      borderRadius: "999px",
                      appearance: "none",
                      fontSize: "14px",
                      minWidth: "100px",
                      cursor: selectedDate ? "pointer" : "not-allowed",
                      backgroundColor: selectedDate ? "#fff" : "#f5f5f5",
                      opacity: selectedDate ? 1 : 0.6
                    }}
                    disabled={!selectedDate}
                  >
                    <option value="">
                      {t('main.new_checkout.view_checkout.shippingOption.select_hour')}
                    </option>
                    {availableHours.map((hour) => (
                      <option key={hour} value={hour}>
                        {hour}
                      </option>
                    ))}
                  </select>
                </div>
              );
            })()}

            {address && (
              <AddressInfo>
                <AddressText>
                  <strong>Entregar em:</strong> {address.address},{' '}
                  {address.number}
                  {address.complement && ` - ${address.complement}`} -{' '}
                  {address.neighborhood} - {address.city}/{address.state} - CEP:{' '}
                  {address.postcode}
                </AddressText>
              </AddressInfo>
            )}
          </ShippingCard>
        );
      })}
    </ShippingOptionsContainer>
  );
};

export default ShippingOptions;
